import React, { useEffect, useState, useCallback, useRef } from "react";
import { useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { inr, ddmmyyyy } from "../lib/format";
import { SectionTitle, Pill, EmptyState, PrimaryButton, ExplainButton, WhyBanner } from "../components/common";
import { PayoutChoice } from "../components/monetization";
import { usePricing, purchase } from "../pricing";
import { payWithRazorpay } from "../lib/razorpay";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Receipt, IndianRupee, Download, Check, ShieldAlert, Wallet } from "lucide-react";

export default function Payments() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const { config: pconf } = usePricing();
  const [payments, setPayments] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [upi, setUpi] = useState("");
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [tds, setTds] = useState(null);

  const load = useCallback(async () => {
    const [p, a] = await Promise.all([api.get("/payments"), api.get("/agreements")]);
    setPayments(p.data); setAgreements(a.data);
    if (a.data[0]) setAmount(String(a.data[0].rent));
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (amount && Number(amount) >= 50000) api.get(`/payments/tds/${amount}`).then((r) => setTds(r.data.tds));
    else setTds(null);
  }, [amount]);

  const isOwner = user?.role === "owner";
  const collected = payments.filter((p) => p.kind === "rent").reduce((s, p) => s + p.amount, 0);
  const deposit = payments.filter((p) => p.kind === "deposit").reduce((s, p) => s + p.amount, 0);

  const busyRef = useRef(false);
  const doPay = async (instant = false) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try {
      if (instant) { try { await purchase("instant_payout", { amount: pconf?.prices?.instant_payout?.amount || 49 }); } catch (e) { console.warn("instant payout note failed", e); } }
      const res = await payWithRazorpay("rent", { agreement_id: agreements[0]?.id });
      if (res?.payment) setReceipt(res.payment);
      setPayOpen(false); await load();
    } catch (e) { if (e?.message !== "cancelled") alert("Payment could not be completed. Please try again."); } finally { setBusy(false); busyRef.current = false; }
  };

  const downloadPdf = async (p) => {
    try {
      const res = await api.get(`/payments/${p.id}/receipt.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `receipt-${p.receipt_no || p.id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { alert("Could not download the receipt PDF."); }
  };

  const downloadLedger = async () => {
    try {
      const res = await api.get("/payments/ledger.xlsx", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "rent-statement.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { alert("Could not download the statement."); }
  };

  return (
    <div className="space-y-5">
      <SectionTitle>{t("nav_payments")}</SectionTitle>

      <div className="grid grid-cols-2 gap-3">
        <div className="gs-card p-4"><div className="text-xs text-slate-400 font-semibold">{isOwner ? t("rent_collected") : t("payment_history")}</div><div className="font-mono font-bold text-xl text-slate-900 mt-1">{inr(collected)}</div></div>
        <div className="gs-card p-4"><div className="text-xs text-slate-400 font-semibold flex items-center gap-1"><Wallet className="w-3 h-3" /> {t("deposit_balance")}</div><div className="font-mono font-bold text-xl text-trust mt-1">{inr(deposit)}</div></div>
      </div>

      {!isOwner && <PrimaryButton onClick={() => setPayOpen(true)} data-testid="open-pay-rent-btn"><IndianRupee className="w-4 h-4 mr-1" /> {t("pay_rent")}</PrimaryButton>}
      {isOwner && <button onClick={downloadLedger} data-testid="download-ledger-btn" className="gs-tap w-full rounded-xl border border-trust text-trust font-semibold text-sm py-2.5 flex items-center justify-center gap-1"><Download className="w-4 h-4" /> Download rent statement (Excel)</button>}

      <div>
        <SectionTitle>{t("payment_history")}</SectionTitle>
        {payments.length === 0 ? <EmptyState icon={Receipt} title={t("payment_history")} /> : (
          <div className="space-y-2.5">
            {payments.map((p) => (
              <div key={p.id} className="gs-card p-3.5 flex items-center gap-3" data-testid={`payment-${p.id}`}>
                <div className="w-10 h-10 rounded-xl bg-trust-light flex items-center justify-center text-trust"><Check className="w-5 h-5" /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-800 capitalize">{p.kind} · {p.month}</div>
                  <div className="text-xs text-slate-400">{p.receipt_no} · {ddmmyyyy(p.created_at)}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono font-bold text-slate-900">{inr(p.amount)}</div>
                  <button onClick={() => setReceipt(p)} data-testid={`view-receipt-${p.id}`} className="text-xs text-terracotta font-semibold flex items-center gap-0.5"><Download className="w-3 h-3" /> {t("receipt")}</button>
                  <button onClick={() => downloadPdf(p)} data-testid={`download-pdf-${p.id}`} className="text-xs text-trust font-semibold flex items-center gap-0.5 mt-0.5"><Download className="w-3 h-3" /> PDF</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pay dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="max-w-[92vw] sm:max-w-md rounded-2xl">
          <DialogHeader><DialogTitle className="font-head text-left">{t("pay_rent")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs font-semibold text-slate-500">{t("rent")}</label><Input type="number" className="rounded-xl gs-tap" data-testid="pay-amount" value={amount} onChange={(e) => setAmount(e.target.value)} /></div>
            <div><label className="text-xs font-semibold text-slate-500">UPI ID</label><Input className="rounded-xl gs-tap" data-testid="pay-upi" placeholder="name@bank" value={upi} onChange={(e) => setUpi(e.target.value)} /></div>
            {tds?.applies && (
              <div className="rounded-xl bg-amberw-light border border-amber-200 p-3 text-sm text-amberw space-y-1" data-testid="tds-notice">
                <div className="flex items-center gap-1.5 font-semibold"><ShieldAlert className="w-4 h-4" /> {t("tds_assistant")}</div>
                <p>Rent is above ₹50,000/month, so you must deduct {tds.rate_percent}% TDS ({inr(tds.amount)}) under {tds.section} and file {tds.form_tenant}. {tds.form_landlord} is generated for your landlord.</p>
                <ExplainButton term="TDS on rent (Section 194-IB)" detail="Tax deducted at source on high rent, deposited to the government." label={t("what_means")} testId="tds-explain-btn" />
              </div>
            )}
            <PayoutChoice amount={pconf?.prices?.instant_payout?.amount || 49} busy={busy} onStandard={() => doPay(false)} onInstant={() => doPay(true)} />
            <WhyBanner>Paying in-app gives you an instant receipt usable for HRA tax exemption.</WhyBanner>
          </div>
        </DialogContent>
      </Dialog>

      {/* Receipt dialog */}
      <Dialog open={!!receipt} onOpenChange={() => setReceipt(null)}>
        <DialogContent className="max-w-[92vw] sm:max-w-md rounded-2xl">
          <DialogHeader><DialogTitle className="font-head text-left flex items-center gap-2"><Receipt className="w-5 h-5 text-terracotta" /> {t("receipt")}</DialogTitle></DialogHeader>
          {receipt && (
            <div className="space-y-2 text-sm" data-testid="receipt-detail">
              <RRow k="Receipt No" v={receipt.receipt_no} /><RRow k="Amount" v={inr(receipt.amount)} /><RRow k="Type" v={receipt.kind} />
              <RRow k="Month" v={receipt.month} /><RRow k="Paid by" v={receipt.payer_name} /><RRow k="Date" v={ddmmyyyy(receipt.created_at)} /><RRow k="Status" v="Success ✓" />
              <div className="rounded-xl bg-trust-light p-3 text-trust text-xs mt-2">{receipt.hra_note}</div>
              {receipt.tds?.applies && <div className="rounded-xl bg-amberw-light p-3 text-amberw text-xs">TDS {receipt.tds.rate_percent}% = {inr(receipt.tds.amount)} · {receipt.tds.section}</div>}
              <PrimaryButton onClick={() => window.print()} data-testid="download-receipt-btn" className="mt-2"><Download className="w-4 h-4 mr-1" /> {t("download_receipt")}</PrimaryButton>
              <button onClick={() => downloadPdf(receipt)} data-testid="download-receipt-pdf-btn" className="gs-tap w-full mt-2 rounded-xl border border-terracotta text-terracotta font-semibold text-sm py-2.5 flex items-center justify-center gap-1"><Download className="w-4 h-4" /> Download PDF</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
const RRow = ({ k, v }) => <div className="flex justify-between border-b border-dashed border-slate-200 pb-1.5"><span className="text-slate-400">{k}</span><span className="font-semibold text-slate-800 capitalize">{v}</span></div>;
